import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const categories = ['All', 'Classes', 'Belt Tests', 'Events', 'Community'];
const galleryImages = [{
  id: 1,
  category: 'Classes',
  title: 'Youth Class Training',
  url: 'https://images.unsplash.com/photo-1555597673-b21d5c935865?w=800'
}, {
  id: 2,
  category: 'Classes',
  title: 'Little Dragons',
  url: 'https://images.unsplash.com/photo-1518619745898-93e765966dcd?w=800'
}, {
  id: 3,
  category: 'Belt Tests',
  title: 'Black Belt Ceremony',
  url: 'https://images.unsplash.com/photo-1595078475328-1ab05d0a6a0e?w=800'
}, {
  id: 4,
  category: 'Events',
  title: 'Community Demo',
  url: 'https://images.unsplash.com/photo-1599058945522-28d584b6f0ff?w=800'
}, {
  id: 5,
  category: 'Classes',
  title: 'Sparring Practice',
  url: 'https://images.unsplash.com/photo-1555597408-26bc8e548a46?w=800'
}, {
  id: 6,
  category: 'Community',
  title: 'Family Night',
  url: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800'
}, {
  id: 7,
  category: 'Belt Tests',
  title: 'Youth Belt Promotion',
  url: 'https://images.unsplash.com/photo-1549719386-74dfcbf7dbed?w=800'
}, {
  id: 8,
  category: 'Events',
  title: 'Tournament Day',
  url: 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=800'
}, {
  id: 9,
  category: 'Classes',
  title: 'Adult Training',
  url: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800'
}, {
  id: 10,
  category: 'Community',
  title: 'School Picnic',
  url: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800'
}, {
  id: 11,
  category: 'Classes',
  title: 'Forms Practice',
  url: 'https://images.unsplash.com/photo-1552196563-55cd4e45efb3?w=800'
}, {
  id: 12,
  category: 'Events',
  title: 'Open House',
  url: 'https://images.unsplash.com/photo-1434682881908-b43d0467b798?w=800'
}];
export function GalleryPage() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [lightboxImage, setLightboxImage] = useState<number | null>(null);
  const filteredImages = selectedCategory === 'All' ? galleryImages : galleryImages.filter(img => img.category === selectedCategory);
  const openLightbox = (id: number) => setLightboxImage(id);
  const closeLightbox = () => setLightboxImage(null);
  const navigateLightbox = (direction: 'prev' | 'next') => {
    if (lightboxImage === null) return;
    const currentIndex = filteredImages.findIndex(img => img.id === lightboxImage);
    let newIndex;
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : filteredImages.length - 1;
    } else {
      newIndex = currentIndex < filteredImages.length - 1 ? currentIndex + 1 : 0;
    }
    setLightboxImage(filteredImages[newIndex].id);
  };
  const currentImage = filteredImages.find(img => img.id === lightboxImage);
  return <main className="pt-20 pb-20 bg-gray-50" data-id="element-411">
      <div className="bg-primary text-white py-16 mb-12" data-id="element-412">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-413">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-414">Gallery</h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto" data-id="element-415">
            See our students in action—from daily classes to belt tests and
            community events.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-416">
        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12" data-id="element-417">
          {categories.map(category => <button key={category} onClick={() => setSelectedCategory(category)} className={`px-6 py-2 rounded-full font-medium transition-all ${selectedCategory === category ? 'bg-primary text-white shadow-md' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'}`} data-id="element-418">
              {category}
            </button>)}
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-419">
          {filteredImages.map((image, index) => <motion.div key={image.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.05
        }} className="group relative aspect-square overflow-hidden rounded-xl bg-gray-200 cursor-pointer shadow-sm hover:shadow-xl transition-shadow" onClick={() => openLightbox(image.id)} data-id="element-420">
              <img src={image.url} alt={image.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-421" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-422">
                <div className="absolute bottom-0 left-0 right-0 p-4" data-id="element-423">
                  <p className="text-white font-semibold" data-id="element-424">{image.title}</p>
                  <p className="text-white/80 text-sm" data-id="element-425">{image.category}</p>
                </div>
              </div>
            </motion.div>)}
        </div>

        {filteredImages.length === 0 && <div className="text-center py-20" data-id="element-426">
            <p className="text-gray-500 text-lg" data-id="element-427">
              No images found in this category.
            </p>
          </div>}
      </div>

      {/* Lightbox */}
      <AnimatePresence data-id="element-428">
        {lightboxImage !== null && currentImage && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" onClick={closeLightbox} data-id="element-429">
            <button onClick={closeLightbox} className="absolute top-4 right-4 text-white hover:text-accent transition-colors z-10" aria-label="Close" data-id="element-430">
              <X className="w-8 h-8" data-id="element-431" />
            </button>

            <button onClick={e => {
          e.stopPropagation();
          navigateLightbox('prev');
        }} className="absolute left-4 text-white hover:text-accent transition-colors z-10" aria-label="Previous" data-id="element-432">
              <ChevronLeft className="w-12 h-12" data-id="element-433" />
            </button>

            <button onClick={e => {
          e.stopPropagation();
          navigateLightbox('next');
        }} className="absolute right-4 text-white hover:text-accent transition-colors z-10" aria-label="Next" data-id="element-434">
              <ChevronRight className="w-12 h-12" data-id="element-435" />
            </button>

            <div className="max-w-6xl w-full" onClick={e => e.stopPropagation()} data-id="element-436">
              <motion.img key={currentImage.id} initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} src={currentImage.url} alt={currentImage.title} className="w-full h-auto max-h-[85vh] object-contain rounded-lg" data-id="element-437" />
              <div className="text-center mt-4" data-id="element-438">
                <p className="text-white text-xl font-semibold" data-id="element-439">
                  {currentImage.title}
                </p>
                <p className="text-white/70" data-id="element-440">{currentImage.category}</p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </main>;
}